import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Draggable from "react-draggable";
import classNames from "classnames";
import { isEmpty } from "lodash";
import { crossIcon, compressIcon, expandIcon } from "../../../../assets/icons";
import {
  EMPTY_STRING,
  DEPLOY,
  ESCAPE,
  KEYDOWN,
  MODAL_SIZE,
  WINDOW_EVENTS,
} from "../../../../constants";
import {
  selectRecentSelectedApp,
  selectAppPipelineStatus,
  selectSelectedApp,
} from "../../../../pages/Applications";
import { capitalizeFirstLetterOfWord } from "../../../../helpers";
import { selectShouldShowDeploymentModal } from "../../Modal";
import Spinner from "../../Spinner";
import { actions as modalActions } from "../../Modal/modal.reducers";
import { DeployLogRenderer, ProgressBar } from "../../index";
import { getPipelineSteps } from "../../AppDrawers/drawers.helpers";
import { selectAlert } from "../../Alert";

const { FRONTEND, BACKEND } = DEPLOY;
const { MD, SM } = MODAL_SIZE;
const { setShouldShowDeploymentModal } = modalActions;
const { POPSTATE } = WINDOW_EVENTS;

const DeploymentLogsModal = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const ref = useRef(null);
  const largerModalRef = useRef(null);
  const maximizeButtonRef = useRef(null);

  const pipelineStatus = useSelector(selectAppPipelineStatus);
  const setShowModal = useSelector(selectShouldShowDeploymentModal);
  const selectRecentApp = useSelector(selectRecentSelectedApp);
  const selectedApp = useSelector(selectSelectedApp);
  const selectedAlert = useSelector(selectAlert);

  const [expandedStep, setExpandedStep] = useState(EMPTY_STRING);
  const [shouldShow, setShouldShow] = useState(false);
  const [size, setSize] = useState(MD);
  const [draggable, setShouldDraggable] = useState(false);
  const [cloudService, setCloudService] = useState();
  const [hasError, setHasError] = useState();

  useEffect(() => {
    selectedAlert && setHasError(selectedAlert.message);
  }, [selectedAlert]);

  useEffect(() => {
    setShouldShow(setShowModal);
  }, [setShowModal]);

  const handleClose = useCallback(() => {
    setShouldShow(false);
    dispatch(setShouldShowDeploymentModal(false));
  }, [dispatch]);

  useEffect(() => {
    const close = ({ code }) => code === ESCAPE && handleClose();
    document.addEventListener(KEYDOWN, close);
    return () => document.removeEventListener(KEYDOWN, close);
  }, [handleClose]);

  useEffect(() => {
    const closeModal = () => {
      handleClose();
      navigate(window.location.path);
    };
    if (shouldShow) window.addEventListener(POPSTATE, closeModal);
    return window.removeEventListener(POPSTATE, closeModal);
  }, [shouldShow, navigate, handleClose]);

  useEffect(() => {
    if (selectRecentApp || !isEmpty(hasError)) {
      const checkIfClickedOutside = (e) => {
        if (maximizeButtonRef.current?.contains(e.target)) setSize(MD);
        else if (
          largerModalRef.current &&
          !largerModalRef.current.contains(e.target)
        )
          setSize(SM);
      };
      document.addEventListener("click", checkIfClickedOutside);
      return () => {
        document.removeEventListener("click", checkIfClickedOutside);
      };
    } else setSize(MD);
  }, [handleClose, selectRecentApp, hasError]);

  useEffect(() => {
    selectRecentApp && setCloudService(selectRecentApp.cloudService);
  }, [selectRecentApp]);

  const getType = (logs) => (logs === FRONTEND ? FRONTEND : BACKEND);

  const shouldShowDeploymentLogs = (logs) =>
    !selectedApp ||
    (selectRecentApp && !(selectRecentApp.redeployed && logs === BACKEND));

  const shouldDisabled = isEmpty(selectRecentApp) && isEmpty(hasError);

  return (
    <>
      {shouldShow && (
        <div>
          {size === SM && (
            <Draggable
              nodeRef={ref}
              bounds="body"
              onStart={() => setShouldDraggable(true)}
              onStop={() => setShouldDraggable(false)}
            >
              <div
                className={classNames(
                  "bg-gray-800 border border-gray-700  z-[110] rounded  mx-auto absolute h-auto w-[300px] bottom-0 right-0",
                  {
                    "cursor-move": draggable,
                  }
                )}
                ref={ref}
              >
                <div className="flex justify-between border-b border-gray-700 p-1">
                  <div className="ml-2">
                    <h6 className="text-sm text-gray-50 font-bold mb-1">
                      {!selectedApp
                        ? "Creating Your Application"
                        : "Deployment Status & Logs"}
                    </h6>
                    {selectRecentApp && (
                      <p className="text-sm text-gray-50">{`Application : ${selectRecentApp?.name}`}</p>
                    )}
                  </div>
                  <div>
                    <button
                      type="button"
                      ref={maximizeButtonRef}
                      className="rounded-md mr-2 bg-gray-400 disabled:cursor-not-allowed"
                      disabled={shouldDisabled}
                    >
                      <img src={expandIcon} alt="min-max" className="w-5 h-5" />
                    </button>
                    <button
                      type="button"
                      className="rounded-md bg-gray-400 disabled:cursor-not-allowed"
                      onClick={() => {
                        handleClose();
                      }}
                      disabled={shouldDisabled}
                    >
                      <img src={crossIcon} alt="cross" className="w-5" />
                    </button>
                  </div>
                </div>
                <div
                  className={classNames("flex-1 p-1", {
                    "flex p-1 items-center justify-center":
                      isEmpty(pipelineStatus),
                  })}
                >
                  <Spinner showSpinner={isEmpty(pipelineStatus)}>
                    <div className="flex space-x-2 justify-between p-3">
                      {pipelineStatus &&
                        Object.keys(pipelineStatus).map(
                          (logs) =>
                            shouldShowDeploymentLogs(logs) && (
                              <div
                                className="flex justify-between space-y-4"
                                key={logs}
                              >
                                <>
                                  <div className="flex space-x-2 p-1 mb-1">
                                    <label className="text-sm text-white mt-2 mb-2">
                                      {`${capitalizeFirstLetterOfWord(
                                        getType(logs)
                                      )}`}
                                    </label>
                                    <div>
                                      <ProgressBar
                                        barType="circle"
                                        logs={pipelineStatus[logs]}
                                        stepsStatus={getPipelineSteps(
                                          getType(logs),
                                          pipelineStatus[logs],
                                          cloudService
                                        )}
                                      />
                                    </div>
                                  </div>
                                </>
                              </div>
                            )
                        )}
                    </div>
                  </Spinner>
                </div>
              </div>
            </Draggable>
          )}
          {size === MD && (
            <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-md z-[999] overflow-auto transition-opacity duration-300">
              <div
                className="bg-gray-800 border border-gray-700 rounded z-[999] mx-auto absolute my-auto sm:w-[600px] h-fit min-h-60 content inset-0"
                ref={largerModalRef}
              >
                <div className="flex justify-between border-b border-gray-700 p-4">
                  <div className="ml-2">
                    <h6 className="text-sm text-gray-50 font-bold mb-1">
                      {!selectedApp
                        ? "Creating Your Application"
                        : "Deployment Status & Logs"}
                    </h6>
                    {selectRecentApp && (
                      <p className="text-sm text-gray-50">{`Application : ${selectRecentApp?.name}`}</p>
                    )}
                  </div>
                  <div>
                    <button
                      type="button"
                      className="rounded-md mr-2 bg-gray-400 disabled:cursor-not-allowed"
                      onClick={() => setSize(SM)}
                      disabled={shouldDisabled}
                    >
                      <img
                        src={compressIcon}
                        alt="min-max"
                        className="w-5 h-5"
                      />
                    </button>
                    <button
                      type="button"
                      className="rounded-md bg-gray-400 disabled:cursor-not-allowed"
                      disabled={shouldDisabled}
                      onClick={() => handleClose()}
                    >
                      <img src={crossIcon} alt="cross" className="w-5" />
                    </button>
                  </div>
                </div>
                {selectedAlert?.message && (
                  <div className="grid border p-2 m-1 rounded relative bg-red-100 border-red-400 text-red-700 justify-center">
                    <div className="block sm:inline  whitespace-pre-line ">
                      {selectedAlert?.message?.customErrorMessage ||
                        selectedAlert?.message}
                    </div>
                  </div>
                )}
                <div
                  className={classNames("flex-1 p-1", {
                    "flex p-1 items-center justify-center":
                      isEmpty(pipelineStatus),
                  })}
                >
                  <Spinner showSpinner={isEmpty(pipelineStatus)}>
                    <div className="min-h-60">
                      {pipelineStatus &&
                        Object.keys(pipelineStatus).map(
                          (logs) =>
                            shouldShowDeploymentLogs(logs) && (
                              <div
                                className="flex justify-between space-y-4 flex-col"
                                key={logs}
                              >
                                <div className="w-full p-2">
                                  <DeployLogRenderer
                                    key={logs}
                                    type={getType(logs)}
                                    setExpandedStep={setExpandedStep}
                                    expandedStep={expandedStep}
                                    logs={pipelineStatus[logs]}
                                    classes="w-auto justify-center overflow-auto scrollbar bg-gray-900 p-1 rounded-md h-48"
                                    shouldShowProgressBar={true}
                                  />
                                </div>
                              </div>
                            )
                        )}
                    </div>
                  </Spinner>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default DeploymentLogsModal;
